import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import { CollapseChevron } from "components/CollapseChevron";
import { forwardRef } from "react";

import { opaqueCardFill } from "./discrepancyTableSx";

/**
 * Fixed height of the sticky section title bar. Local now: it used to be the
 * offset the tables gave their own sticky column headers, and nothing inside a
 * table pins any more.
 */
const SECTION_TITLE_HEADER_HEIGHT = "2.75rem";

export interface DiscrepancySectionProps {
  title: string;
  count: number;
  /**
   * Shown in the header instead of the bare `count` when given — e.g.
   * `"3 pending review · 1 fix pending"`. `count` still drives the `isEmpty` check.
   */
  countLabel?: string;
  /** While loading the count isn't known yet — render children so their own skeletons show. */
  loading?: boolean;
  /** Shows a small red dot next to the title — used for time-critical items. */
  hasUrgent?: boolean;
  /** Hides the body while keeping the header (title + count) visible. Defaults to expanded. */
  collapsed?: boolean;
  /** Required when the header should be clickable to toggle `collapsed`. */
  onToggleCollapse?: () => void;
  /**
   * Shown as a "N selected" chip on the header whenever it's collapsed —
   * selecting rows, then collapsing the section, shouldn't make the
   * selection invisible. Omit for sections with no row selection.
   */
  selectedCount?: number;
  /**
   * Sizes the card to its container rather than to its contents, and drops the
   * horizontal inset.
   *
   * The default suits the staff review panel, where a section wraps a table
   * that scrolls horizontally: the card grows with the table and floats inset
   * from the panel's edges. The customer dialog stacks plain cards instead, and
   * sits them under a status glossary and a summary bar that each run the full
   * width of the dialog. There the default gives a ragged right edge — the
   * sections sit 24px narrower than everything above them, and a section whose
   * cards hold long text (an EPA correction's § quote) grows wider than one
   * whose cards do not, so two sections in the same dialog end up different
   * widths.
   */
  fillContainer?: boolean;
  /** Overrides the default "Nothing to review here." placeholder. */
  children: React.ReactNode;
}

/**
 * A top-level section in the Discrepancy Review panel, framed as a card on
 * `background.paper` against the scroll container's `palette.canvas` ground,
 * so stacked sections read as distinct blocks. The header (title + count) always
 * renders, even at 0 — whether the section itself is shown at all is the
 * caller's call (the summary tiles). The header carries the card's own fill and
 * a rule beneath it, sticks to the top of the scroll container while its
 * section is in view, and is clickable to collapse/expand the body. When there's nothing to
 * show, a placeholder replaces the children so an empty section doesn't leave
 * a table with headers and no rows — and the chevron shows the same "closed"
 * orientation as a manually-collapsed section, since neither has an expanded
 * body to look at.
 */
export const DiscrepancySection = forwardRef<HTMLDivElement, DiscrepancySectionProps>(
  function DiscrepancySection(
    {
      title,
      count,
      countLabel,
      loading = false,
      hasUrgent = false,
      collapsed = false,
      onToggleCollapse,
      selectedCount,
      fillContainer = false,
      children,
    },
    ref
  ) {
    const theme = useTheme();
    const isEmpty = !loading && count === 0;
    const looksClosed = collapsed || isEmpty;
    // The band is sticky, so its hover has to stay opaque — `action.hover` is
    // translucent and would let rows scroll through the title.
    const bandHoverBg =
      theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100];

    let body: React.ReactNode = children;
    if (collapsed) {
      body = null;
    } else if (isEmpty) {
      body = (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 2 }}>
          Nothing to review here.
        </Typography>
      );
    }

    return (
      <Paper
        ref={ref}
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 2,
          ...(fillContainer
            ? // One width for every section, set by the container, so a stack of
              // them shares an edge with each other and with anything else the
              // dialog lays out at full width.
              { width: "100%" }
            : {
                // mx matches the dialog title's inset, so a card's left edge lines up
                // with the dialog's own heading.
                mx: 3,
                // Grow with wide (horizontally scrolling) tables; never narrower
                // than the panel (minus the mx gutters above).
                width: "fit-content",
                minWidth: "calc(100% - 48px)",
              }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          onClick={onToggleCollapse}
          sx={{
            minHeight: SECTION_TITLE_HEADER_HEIGHT,
            position: "sticky",
            top: 0,
            // Above the table rows that scroll under it. This band is the only
            // thing in the dialog that pins: a table's column headers and its
            // field group headers used to pin too, at fixed offsets below this
            // one, and every group header in a section declared the same
            // offset — so they stacked on each other and hid the rows between
            // them.
            zIndex: 3,
            // The card's own fill, not a tint of it. A grey band here landed 2
            // points from the `canvas` panel behind the card, so the card's top
            // edge read as part of the panel. The rule below the band is what
            // separates the title from the table under it.
            ...opaqueCardFill(theme),
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
            // Square while a body follows; rounded when the header is the whole card.
            borderBottomLeftRadius: collapsed ? "inherit" : 0,
            borderBottomRightRadius: collapsed ? "inherit" : 0,
            // No rule only when the header is the whole card. `collapsed`, not
            // `looksClosed`: an empty section still renders its placeholder
            // below the header, which needs the rule as much as a table does.
            borderBottom: collapsed ? "none" : `1px solid ${theme.palette.divider}`,
            ...(onToggleCollapse ? { cursor: "pointer", "&:hover": { bgcolor: bandHoverBg } } : {}),
          }}
        >
          {/* Inner sticky wrapper pins the title to the left edge during horizontal scroll. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ position: "sticky", left: 0, px: 2 }}
          >
            {onToggleCollapse && <CollapseChevron collapsed={looksClosed} />}
            {hasUrgent && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "error.dark",
                  flexShrink: 0,
                }}
              />
            )}
            {/* Same overline treatment as the manifest drawer's SectionHeader;
                component=h6 keeps the heading role the overline span would lose. */}
            <Typography
              variant="overline"
              color="text.primary"
              component="h6"
              // Darker and heavier than the 42 other overline labels in oak, matching
              // SeverityProvenance and violationDisplay. The design makes these headers
              // prominent through colour; weight carries it without a new token.
              sx={{ fontWeight: 700 }}
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              {countLabel ?? count}
            </Typography>
            {collapsed && !!selectedCount && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${selectedCount} selected`}
                sx={{ height: 20, fontSize: "0.75rem" }}
              />
            )}
          </Stack>
        </Stack>
        {body}
      </Paper>
    );
  }
);
